import clsx from 'clsx'
import {cloneDeep, isEmpty, isEqual, mapValues, omit} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {KTSVG} from '../../../../../_gori/helpers'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import {DropdownButton} from '../../../../../_gori/partials/widgets'
import {SettingsConfig} from '../../../settings/core/_const'
import SettingsService from '../../../settings/core/_requests'
import {OrdersConfig} from '../../core/_const'
import {DIMENSION_UNIT, ROUTES, WEIGHT_UNIT} from '../../../../../_gori/constants'

type Props = {
  formik: any
  disabled: boolean
  fromCreateOrder: boolean
  dataCopy: any
  setDataRates: any
  renderFirst: any
  setPresetService?: (service: string | undefined) => void
}

const PresetApplyButton: React.FC<Props> = ({
  formik,
  disabled = false,
  fromCreateOrder,
  dataCopy,
  setDataRates,
  renderFirst,
  setPresetService,
}) => {
  const intl = useIntl()
  const params = useParams()
  const navigate = useNavigate()
  const {newCancelToken, isCancel} = useCancelToken()
  const [dataPresetDefault, setDataPresetDefault] = useState([])
  const {statesGlobal, getPresets} = useStatesGlobal()
  const presets = statesGlobal.presets

  const isA1 = useMemo(() => {
    return !!formik.values?.a1_batch_id
  }, [formik.values?.a1_batch_id])

  // BEGIN: Action Apply Preset
  const getPresetDefault = useCallback(async () => {
    try {
      const {default_preset} = await SettingsService.getDefaultPreset({
        cancelToken: newCancelToken(),
      })
      setDataPresetDefault(default_preset)
    } catch (error) {
      if (isCancel(error)) return
    }
  }, [isCancel, newCancelToken, setDataPresetDefault])

  const optionsPreset = useMemo(() => {
    const handleApplyPreset = async (item) => {
      formik.setErrors({})

      const applyAddress = (name) => {
        const result: any = Object.entries(OrdersConfig.SHIPPING).reduce(
          (initValue, [key, value]) => {
            switch (value.value) {
              case OrdersConfig.SHIPPING.RESIDENTIAL.value:
                initValue[`${name}_${value.value}`] =
                  item?.shipping_options?.[`${name}`]?.[`${name}_${value.value}`] || false
                break
              case OrdersConfig.SHIPPING.VERIFIED.value:
              case OrdersConfig.SHIPPING.SAVE_ADDRESS.value:
                break
              default:
                initValue[`${name}_${value.value}`] =
                  item?.shipping_options?.[`${name}`]?.[`${name}_${value.value}`] || ''
                break
            }
            return initValue
          },
          {}
        )
        formik.setFieldValue(name, result)
      }

      const applyService = () => {
        const service = item?.shipping_options?.service ?? null
        if (service) {
          formik.setFieldValue('service', service)
          setDataRates((prev) => {
            if (!prev) return null

            const chooseRate =
              prev?.options?.find((rate) => isEqual(rate.carrier_service, service)) || null

            formik.setFieldValue('estimated_rate', chooseRate?.rate || null)
            formik.setFieldValue('is_cheapest_rate', chooseRate?.cheapest || false)

            return {
              ...prev,
              chooseRate,
            }
          })
          setPresetService?.(service)
        }
      }
      const applyParcel = () => {
        const parcels = Object.entries(formik.values.parcels as Record<string, any>).reduce(
          (result, [key, initParcel]) => {
            const parcel = Object.keys(initParcel as Record<string, any>).reduce(
              (parcelResult, parcelKey) => {
                const value = item?.shipping_options?.parcels?.[0]?.[parcelKey]
                if (value !== undefined && value !== null && value !== '') {
                  parcelResult[parcelKey] = value
                }
                return parcelResult
              },
              {} as Record<string, any>
            )
            result[key] = {...initParcel, ...parcel, package_type: '', package_id: null}
            return result
          },
          {} as Record<string, any>
        )
        formik.setFieldValue('parcels', parcels)
      }

      if (!isEmpty(item?.shipping_options?.from?.from_company) && !isA1) {
        await applyAddress('from')
      }

      if (!isEmpty(item?.shipping_options?.to?.to_company)) {
        await applyAddress('to')
      }

      if (!isEmpty(item?.shipping_options?.service)) {
        await applyService()
      }

      await applyParcel()
    }
    const optionPresetApi = !isEmpty(presets)
      ? presets.map((item: any) => ({
          label: item?.name,
          value: item.id,
          action: () => handleApplyPreset(item),
          className: 'cursor-pointer',
        }))
      : []

    if (!params?.id && renderFirst.current && !isEmpty(dataPresetDefault) && !dataCopy) {
      dataPresetDefault.forEach((item: any) => {
        if (item.order_type === SettingsConfig.DEFAULT_PRESETS.SHIPBAE.value) {
          handleApplyPreset(item.preset)
        }
      })
      renderFirst.current = false
    }

    return [
      ...optionPresetApi,
      {
        label: <span className='text-primary'>{intl.formatMessage({id: 'MANAGE_PRESETS'})}</span>,
        action: () => {
          navigate(ROUTES.SETTINGS.PRESETS)
        },
        className: 'cursor-pointer bg-hover-light-primary',
      },
    ]
  }, [
    presets,
    params?.id,
    renderFirst,
    dataPresetDefault,
    dataCopy,
    intl,
    formik,
    isA1,
    setDataRates,
    navigate,
    setPresetService,
  ])

  useEffect(() => {
    if (fromCreateOrder) return
    getPresets()
    if (!params?.id && isEmpty(dataCopy)) {
      getPresetDefault()
    }

    return () => {}
  }, [fromCreateOrder, getPresetDefault, getPresets, params?.id, dataCopy])
  // END: Action Apply Preset

  // BEGIN: Set Preset Id for order
  const convertString = (inputObject) => {
    return mapValues(inputObject, (value) => (typeof value === 'number' ? value.toString() : value))
  }

  useEffect(() => {
    if (isEmpty(presets)) return
    const foundPreset = presets?.filter((preset) => {
      const initParcel = {
        package_id: null,
        package_type: '',
        length: '',
        width: '',
        height: '',
        weight: '',
        dimension_unit: DIMENSION_UNIT.IN,
        weight_unit: WEIGHT_UNIT.OZ,
        package_count: 1,
      }
      const _presetParcel = cloneDeep({...initParcel, ...preset?.shipping_options?.parcels?.[0]})
      const checkEqualParcels = isEqual(
        convertString(omit(cloneDeep(formik.values.parcels[0]), ['weight_limit'])),
        convertString(omit(cloneDeep(_presetParcel), ['weight_limit']))
      )
      const checkEqualFrom = isEqual(
        omit(cloneDeep(formik.values.from), ['from_is_verified', 'from_type']),
        omit(cloneDeep(preset?.shipping_options?.from), ['from_is_verified', 'from_type'])
      )
      const checkEqualTo = isEqual(
        omit(cloneDeep(formik.values.to), ['to_is_verified', 'to_type']),
        omit(cloneDeep(preset?.shipping_options?.to), ['to_is_verified', 'to_type'])
      )
      const checkEqualService = isEqual(formik.values.service, preset?.shipping_options?.service)
      if (checkEqualParcels && checkEqualFrom && checkEqualTo && checkEqualService) {
        return preset
      }
      return null
    })

    formik.setFieldValue('preset_id', foundPreset?.[0]?.id || null)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.parcels, formik.values.service, formik.values.from, formik.values.to])
  // END: Set Preset Id for order

  return (
    <>
      <div
        className={clsx('fw-bolder', {
          'cursor-no-drop': disabled,
          'cursor-pointer': !disabled,
        })}
      >
        <DropdownButton
          className={clsx('btn btn-advanced-tertiary p-1 ps-3 fs-inherit fw-bolder', {
            'pe-none': disabled,
          })}
          title={intl.formatMessage({id: 'APPLY_PRESET'})}
          icon={<KTSVG path='/media/gori/arrows/arrow_down.svg' className='svg-icon-2' />}
          value={formik.values?.preset_id}
          list={optionsPreset}
        />
      </div>
    </>
  )
}

export {PresetApplyButton}
